import { Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormControl, ValidationErrors } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TNil } from '@app/app.interface';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { EPermissions } from '@enums/permissions.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseComponent } from '@shared/components/base.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { ImageLoaderDirective } from '@shared/directives/image-loader.directive';
import { ImageUtilitiesService, TFileResourceType } from '@utilities/image-utilities.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { finalize, Observable, Subscription } from 'rxjs';
import { ICustomFile, IFileUploadState } from './file-uploader.interface';
import { FileUploaderService } from './file-uploader.service';

interface IRemoteFileMeta {
  fileName: string | null;
  mimeType: string | null;
  size: number | null;
}

@Component({
  imports: [
    HasPermissionDirective,
    IconComponent,
    ImageLoaderDirective,
    MatButtonModule,
    MatErrorComponent,
    MatFormFieldModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    NgClass,
    NgTemplateOutlet,
    NgxSkeletonLoaderModule,
    OverlayModule,
    TranslateModule,
  ],
  selector: 'app-file-uploader',
  styleUrl: './file-uploader.component.scss',
  templateUrl: './file-uploader.component.html',
})
export class FileUploaderComponent extends BaseComponent implements OnDestroy {
  private static readonly documentFileExtensions = ['csv', 'doc', 'docx', 'pdf', 'ppt', 'pptx', 'txt', 'xls', 'xlsx'];
  private static readonly imageFileExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

  public readonly fileInputRef = viewChild<ElementRef<HTMLInputElement>>('fileInput');
  private readonly enlargedImageOverlayTemplate = viewChild<TemplateRef<void>>('enlargedImageOverlay');
  private readonly listPreviewOverlayTemplate = viewChild<TemplateRef<void>>('listPreviewOverlay');
  private readonly thumbnailImageRef = viewChild<ElementRef<HTMLImageElement>>('thumbnailImage');

  public readonly allowDocumentUploads = input<boolean>(false);
  public readonly clearAfterUpload = input<boolean>(false);
  public readonly displayMode = input<'list' | 'media'>('media');
  public readonly fileExtensions = input<string[] | null>(null);
  public readonly size = input<'small' | 'big'>('small');
  public readonly shape = input<'circle' | 'rounded'>('rounded');
  public readonly imageWidth = input<number>(100);
  public readonly imageHeight = input<number>(100);
  public readonly id = input<string | null>(null);
  public readonly imageId = input<string | null>(null);
  public readonly fileForm = input<FormControl<ICustomFile | null> | null>(null);
  public readonly title = input<string>();
  public readonly description = input<string | null>(null);
  public readonly displayRevertButton = input<boolean>(false);
  public readonly isExclusive = input<string | TNil>(null);
  public readonly disabled = input<boolean>(false);
  public readonly isViewMode = input<boolean>(false);
  public readonly requiredPermission = input<EPermissions | EPermissions[]>([]);
  public readonly showEditButton = input<boolean>(false);
  public readonly showImagePreview = input<boolean>(true);
  public readonly uploadRequest = input<((file: File, fileType: 'file' | 'image') => Observable<string>) | null>(null);

  public readonly imageSelected = output<{ file: File; id: string }>();
  public readonly imageRemoved = output<void>();
  public readonly reverted = output<void>();

  public readonly isDragging = signal<boolean>(false);

  public readonly uploadedImage = computed(() => this.state().file || this.file());
  public readonly isUploading = computed(() => this.state().isUploading);
  public readonly currentImageId = computed(() => this.state().imageId);
  public readonly shouldShowSpinner = computed<boolean>(() => {
    const hasImageSource = !!(
      this.currentImageUrl() ||
      this.processedImageUrl() ||
      (this.currentImageId() && this.isRemoteFileId())
    );

    return hasImageSource && !this.isUploading();
  });
  public readonly currentImageUrl = computed(() => {
    const file = this.uploadedImage();

    if (!file || file.isMockFile) {
      return null;
    }

    this.objectUrl = URL.createObjectURL(file);

    return this.objectUrl;
  });

  public readonly imageSrc = computed<string | null>(() => {
    const fileUrl = this.currentImageUrl();

    if (fileUrl) {
      return fileUrl;
    }

    if (this.isRemoteFileId()) {
      return this.currentImageId();
    }

    return this.processedImageUrl();
  });

  // A remote file/image id is normally an opaque backend-issued UUID (the `files` table's
  // primary key) rather than a base64-encoded storage path - see ImageUtilitiesService.
  // isValidFileId. There is no longer a frontend-visible distinction between a "temp"/pending id
  // and a permanently-attached one (see onRemoveImage below): the backend's own cleanup task now
  // owns reclaiming a file that was uploaded but never attached, based on the file row's own
  // status/expires_at.
  //
  // Also true for a legacy base64 id (see ImageUtilitiesService.isValidLegacyFileId) - old
  // deliverables/icons created before the id-format migration can still hold one of these, and
  // nothing backfills that data, but the backend's read endpoints resolve both formats. This is a
  // read-only display concern: it only decides whether to fetch/show the file, never whether one
  // can be uploaded/attached/removed here (those flows only ever produce/expect a modern UUID).
  public readonly isRemoteFileId = computed(() => {
    const imageId = this.currentImageId();

    return imageId ? ImageUtilitiesService.isDisplayableFileId(imageId) : false;
  });

  public readonly processedImageUrl = computed(() => {
    const imageId = this.currentImageId();

    if (!imageId || this.isRemoteFileId()) {
      return null;
    }

    const isCircle = this.shape() === 'circle';
    const circleSize = this.circleSize();

    const width = isCircle ? circleSize : this.imageWidth();
    const height = isCircle ? circleSize : this.imageHeight();

    return ImageUtilitiesService.replaceImageDimensions(imageId, width, height);
  });

  public readonly circleSize = computed<number>(() => {
    const width = this.imageWidth();
    const height = this.imageHeight();

    return Math.min(width, height);
  });

  // Name/extension used to be decoded synchronously out of the base64-encoded id itself (see git
  // history). A remote id carries no such information anymore, so for a remote attachment these
  // now read from remoteFileMeta - populated asynchronously by syncRemoteFileMeta() below, either
  // from the file read endpoint's Content-Disposition response header ('media' mode, piggybacked
  // on the exact same request/cache entry ImageLoaderDirective already makes to display the
  // pixels - see getImageUrlAndSize) or from the same header on the one-shot fetch 'list' mode
  // already made for the file size ('list' mode never displays pixels, so there's no shared
  // request to piggyback on).
  //
  // A real local File the user just picked always wins over that remote lookup, checked first
  // and unconditionally - mirroring currentFileType() above. Right after a successful upload,
  // state().imageId already holds the new remote id (so isRemoteFileId() is true) but state().file
  // is deliberately never cleared (see the upload success handler below), so without this
  // check-local-first ordering these would ignore the local File's already-known name/extension
  // and depend entirely on remoteFileMeta resolving - which used to surface as a wrong answer
  // (the "select media to upload" placeholder, or an empty extension) for as long as that fetch
  // was pending, even though the real answer was sitting right there in uploadedImage().
  //
  // Only once there's no usable local file left (e.g. mounting fresh against an id from a
  // previous session, with nothing left to read synchronously) do these fall through to
  // remoteFileMeta - see isFileMetadataLoading below for how that pending window is now
  // surfaced honestly instead of showing a misleading placeholder.
  // A displayed name - local or remote - can itself carry a leading "{uuid}_" storage-key prefix
  // (see ImageUtilitiesService.stripUuidPrefix) rather than the original name a person would
  // recognize, so every branch below strips it right before returning, after the extension has
  // already been split off (stripUuidPrefix only ever looks at the part before the first
  // underscore, so running it before the extension split would work too, but running it after
  // keeps this matching fileExtension()'s own split-then-use ordering below).
  public readonly fileName = computed(() => {
    this.isStateReset();

    const file = this.uploadedImage();

    if (file && !file.isMockFile) {
      const localFileName = file.name ?? this.fileForm()?.value?.name;
      const localName = localFileName !== undefined ? FileUploaderService.getFileBaseName(localFileName) : undefined;

      return localName !== undefined
        ? ImageUtilitiesService.stripUuidPrefix(localName)
        : (this.title() ?? this.translate.instant('components.fileUploader.selectMediaToUpload'));
    }

    const currentImageId = this.currentImageId();

    if (currentImageId && this.isRemoteFileId()) {
      const remoteFileName = this.remoteFileMeta()?.fileName;

      if (remoteFileName) {
        const baseName = remoteFileName.includes('.')
          ? remoteFileName.split('.').slice(0, -1).join('.')
          : remoteFileName;

        return ImageUtilitiesService.stripUuidPrefix(baseName);
      }

      return this.translate.instant('components.fileUploader.selectMediaToUpload');
    }

    const fallbackFileName = this.uploadedImage()?.name ?? this.fileForm()?.value?.name;
    const fallbackName =
      fallbackFileName !== undefined ? FileUploaderService.getFileBaseName(fallbackFileName) : undefined;

    return fallbackName !== undefined
      ? ImageUtilitiesService.stripUuidPrefix(fallbackName)
      : (this.title() ?? this.translate.instant('components.fileUploader.selectMediaToUpload'));
  });

  public readonly fileExtension = computed(() => {
    this.isStateReset();

    const file = this.uploadedImage();

    if (file && !file.isMockFile) {
      return FileUploaderService.getFileExtension(file.name ?? this.fileForm()?.value?.name ?? '');
    }

    const currentImageId = this.currentImageId();

    if (currentImageId && this.isRemoteFileId()) {
      const remoteFileName = this.remoteFileMeta()?.fileName;
      const parts = remoteFileName?.split('.') ?? [];

      return parts.length > 1 ? parts[parts.length - 1] : '';
    }

    return FileUploaderService.getFileExtension(this.uploadedImage()?.name ?? this.fileForm()?.value?.name ?? '');
  });

  // True only while genuinely waiting on remoteFileMeta to settle for the name/extension shown
  // above: no local file to read synchronously, a valid remote id, and neither a result nor an
  // error has landed yet. Drives the template's skeleton loaders in place of fileName()'s
  // "select media to upload" placeholder and fileExtension()'s misleading "here's what you're
  // allowed to upload" fallback text, both of which used to show while the real answer was still
  // in flight - see the file-list/media template below.
  //
  // This can never get stuck showing a skeleton forever: syncRemoteFileMeta's fetch (either
  // getImageUrlAndSize for 'media' mode or getFileMetadataInfo for 'list' mode) always settles one
  // way or the other, because both paths are bounded by a timeout() operator upstream (see
  // IMAGE_FETCH_TIMEOUT_MS/FILE_FETCH_TIMEOUT_MS and ImageUtilitiesService.getFileMetadataInfo) -
  // a request that errors, times out, or hangs indefinitely all still end up calling either
  // remoteFileMeta.set(...) or hasRemoteFileMetaError.set(true) within a bounded window, and this
  // flips to false the instant either one does.
  public readonly isFileMetadataLoading = computed<boolean>(() => {
    const file = this.uploadedImage();

    if (file && !file.isMockFile) {
      return false;
    }

    const currentImageId = this.currentImageId();

    if (!currentImageId || !this.isRemoteFileId()) {
      return false;
    }

    return this.remoteFileMeta() === null && !this.hasRemoteFileMetaError();
  });

  public readonly fileSizeLabel = computed<string | null>(() => {
    this.isStateReset();

    const file = this.uploadedImage();

    if (file && !file.isMockFile) {
      const sizeInBytes = typeof file.size === 'number' ? file.size : 0;

      if (sizeInBytes <= 0) {
        return null;
      }

      return FileUploaderService.formatBytes(sizeInBytes);
    }

    const hasRemoteFileMetaError = this.hasRemoteFileMetaError();

    if (hasRemoteFileMetaError) {
      return `${this.translate.instant('shared.na')} KB`;
    }

    const remoteFileSize = this.remoteFileMeta()?.size ?? null;

    if (remoteFileSize !== null) {
      return FileUploaderService.formatBytes(remoteFileSize);
    }

    return null;
  });
  // The backend used to embed the extension in the id, so the displayed file/document icon could
  // be derived purely from that. A remote id no longer carries this, so this now prefers the
  // mime-type the backend reports (Content-Type header, read via the same async metadata fetch as
  // fileName above) once it resolves, falling back to extension-based detection for a real local
  // File the user just picked (which does still carry its own name). Before either is known
  // (freshly mounted with a remote id, metadata not fetched yet), the default depends on
  // displayMode: 'media' mode is image-only by design (avatars/banners never show anything else),
  // so 'image' is always a safe guess there. 'list' mode is the one place mixed image/document
  // attachments are shown (briefing notes), so defaulting it to 'image' too used to make a
  // not-yet-confirmed document register as isCurrentFileImage()/hasPreview() before its real type
  // was known - showing a preview affordance for a document, and letting openListPreview()/
  // onDownloadFile() below request it through the image-typed endpoint (Accept: image/*) purely
  // because metadata hadn't resolved yet. 'file' is the safe default there instead: both
  // consumers re-render with the correct type/icon the instant the fetch resolves either way.
  public readonly currentFileType = computed<TFileResourceType>(() => {
    const file = this.uploadedImage();

    if (file && !file.isMockFile) {
      return FileUploaderService.getFileTypeByFile(file);
    }

    const remoteMimeType = this.remoteFileMeta()?.mimeType;

    if (remoteMimeType) {
      return FileUploaderService.getFileTypeByMimeType(remoteMimeType);
    }

    return this.displayMode() === 'list' ? 'file' : 'image';
  });
  public readonly isCurrentFileImage = computed(() => this.currentFileType() === 'image');
  public readonly listModeIcon = computed(() => {
    if (this.isCurrentFileImage()) {
      return '/icons/photo.svg';
    }

    return '/icons/file-description.svg';
  });
  public readonly hasPreview = computed(() => {
    return this.showImagePreview() && this.isCurrentFileImage() && !!this.currentImageId();
  });
  public readonly listPreviewSafeUrl = computed<SafeResourceUrl | null>(() => {
    const listPreviewUrl = this.listPreviewUrl();

    if (!listPreviewUrl) {
      return null;
    }

    return this.sanitizer.bypassSecurityTrustResourceUrl(listPreviewUrl);
  });

  public readonly placeholderImage = '/icons/image-placeholder.svg';
  public readonly allowedFileExtensions = computed<string[]>(() => {
    const fileExtensions = this.fileExtensions();

    if (fileExtensions?.length) {
      return fileExtensions.map((extension) => extension.toLowerCase());
    }

    if (this.allowDocumentUploads()) {
      return FileUploaderComponent.documentFileExtensions;
    }

    return FileUploaderComponent.imageFileExtensions;
  });
  public readonly fileAccept = computed(() => {
    const allowedFileExtensions = this.allowedFileExtensions();

    if (
      allowedFileExtensions.length === FileUploaderComponent.imageFileExtensions.length &&
      FileUploaderComponent.imageFileExtensions.every((extension) => allowedFileExtensions.includes(extension))
    ) {
      return 'image/*';
    }

    return allowedFileExtensions.map((extension) => `.${extension}`).join(',');
  });

  public imageLoaded = false;
  public displayError = false;
  public isImageEnlarged = false;
  public imageError = false;
  public readonly isListPreviewOpened = signal<boolean>(false);
  public readonly listPreviewType = signal<'file' | 'image' | null>(null);
  public readonly listPreviewUrl = signal<string | null>(null);

  private readonly state = signal<IFileUploadState>({
    file: null,
    imageId: null,
    isUploading: false,
  });
  private readonly file = signal<ICustomFile | null>(null);
  // Populated by syncRemoteFileMeta() below - the async replacement for what used to be decoded
  // synchronously out of the id (name/extension/mime-type/size all now only ever come from the
  // backend, see fileName()/fileExtension()/currentFileType()/fileSizeLabel() above).
  private readonly remoteFileMeta = signal<IRemoteFileMeta | null>(null);
  private readonly hasRemoteFileMetaError = signal<boolean>(false);
  private readonly remoteFileMetaId = signal<string | null>(null);
  private readonly isStateReset = signal<boolean>(false);

  private objectUrl?: string;
  private lastImageSrc: string | null = null;
  private preventZoom = false;
  private listPreviewObjectUrl?: string;
  private mediaEnlargeOverlayRef?: OverlayRef;
  private listPreviewOverlayRef?: OverlayRef;
  // The cacheKey syncRemoteFileMeta() got back from ImageUtilitiesService.getImageUrlAndSize when
  // it last shared ImageLoaderDirective's request/cache entry for 'media' mode - mirrors that
  // directive's own cachedKey field/contract exactly (see its comment): captured once at acquire
  // time, held as-is, and released via releaseRemoteFileMetaState below rather than re-derived
  // later, since re-deriving depends on the current route and can resolve to the wrong entry by
  // the time cleanup runs.
  private mediaMetaCacheKey?: string;
  // syncRemoteFileMeta()'s current fetch subscription - the one-shot getFileMetadataInfo call in
  // 'list' mode, or the shareReplay-backed getImageUrlAndSize subscription in 'media' mode (the
  // two are mutually exclusive per call, so sharing this one field is safe). Neither is otherwise
  // cancelled or cleaned up anywhere, so without tracking and unsubscribing it here it would keep
  // a subscriber (and this component) alive past both a mid-flight id change and the component's
  // own destruction. The applyRemoteFileMeta/applyRemoteFileMetaError id guard already prevents a
  // stale response from being applied, so this is about not leaking the subscription itself
  // rather than about correctness of the applied value.
  private remoteFileMetaSubscription?: Subscription;

  private readonly enlargedImageSrcOverride = signal<string | null>(null);
  public readonly enlargedOverlayImageFailed = signal<boolean>(false);
  public readonly enlargedOverlayImageReady = signal<boolean>(false);

  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);

  constructor(
    private readonly imageService: ImageUtilitiesService,
    private readonly sanitizer: DomSanitizer,
    private readonly translate: TranslateService,
  ) {
    super();

    effect(() => {
      const inputImageId = this.imageId();
      const inputFile = this.file();

      this.updateState({ file: inputFile, imageId: inputImageId });
    });

    // Drops a stale local File once there's a real remote id to fall back on instead (e.g.
    // replacing an already-uploaded image: the old id is still around while the new local file
    // uploads). Deliberately keyed off currentImageId() alone, not currentImageUrl() - a File
    // that has no id at all yet (mid-upload, or a caller like the brief uploader whose
    // uploadRequest never produces a real id by design) must stay as the only thing there is to
    // show, not get nulled out from under it.
    effect(() => {
      if (this.currentImageId() && !this.isRemoteFileId()) {
        this.file.set(null);
        this.updateState({ file: null });
      }
    });

    effect(() => {
      const src = this.imageSrc();

      if (src !== this.lastImageSrc) {
        this.imageLoaded = false;
        this.imageError = false;
        this.lastImageSrc = src ?? null;
      }
    });

    effect(() => {
      if (this.fileForm()) {
        this.fileForm()?.valueChanges.subscribe((file) => {
          this.file.set(file);
        });

        // A list row is handed an already-filled control (e.g. a local brief file that never gets a
        // remote id), so it has to read the current value - valueChanges only reports later ones.
        if (this.isViewMode() || this.displayMode() === 'list') {
          this.file.set(this.fileForm()!.value);
        }
      }
    });

    effect(() => {
      this.syncRemoteFileMeta();
    });
  }

  public ngOnDestroy(): void {
    this.detachMediaEnlargeOverlay();
    this.detachListPreviewOverlay();
    this.releaseRemoteFileMetaState();
  }

  public onFileSelected(event: Event): void {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];

    this.uploadFile(file, event);
  }

  public onUploadNewClick(fileInput?: HTMLInputElement): void {
    this.preventZoom = true;

    if (this.disabled() || this.isViewMode()) {
      return;
    }

    if (fileInput) {
      fileInput.click();

      return;
    }

    if (this.fileInputRef()) {
      this.fileInputRef()!.nativeElement.click();
    }
  }

  // No longer distinguishes a "temp"/pending attachment from a permanently-attached one, and no
  // longer sends a delete request itself either way: the backend's own cleanup task now reclaims
  // an uploaded-but-never-attached file based on the file row's own status/expires_at, so removing
  // an attachment here is just detaching it from this form - the same thing this used to do only
  // for the non-temp case.
  public onRemoveImage(event?: Event): void {
    if (this.disabled() || this.isViewMode()) {
      return;
    }

    event?.stopPropagation();

    this.resetState();
    this.imageRemoved.emit();
  }

  public onImageEnlarge(): void {
    if (this.preventZoom) {
      this.preventZoom = false;

      return;
    }

    if (this.currentImageId() || this.currentImageUrl()) {
      this.isImageEnlarged = true;
      this.attachMediaEnlargeOverlay();
    }
  }

  public closeEnlargedImage(event?: Event): void {
    event?.stopPropagation();

    this.isImageEnlarged = false;
    this.detachMediaEnlargeOverlay();
  }

  public closeListPreview(event?: Event): void {
    event?.stopPropagation();
    this.detachListPreviewOverlay();
    this.isListPreviewOpened.set(false);
    this.listPreviewType.set(null);
    this.listPreviewUrl.set(null);

    if (this.listPreviewObjectUrl) {
      URL.revokeObjectURL(this.listPreviewObjectUrl);
      this.listPreviewObjectUrl = undefined;
    }
  }

  public openListPreview(event?: Event): void {
    event?.stopPropagation();

    if (!this.hasPreview()) {
      return;
    }

    const currentImageId = this.currentImageId();

    if (!currentImageId) {
      return;
    }

    const currentFileType = this.currentFileType();

    this.imageService.getFile(currentImageId, currentFileType).subscribe({
      next: (fileUrl) => {
        if (this.listPreviewObjectUrl) {
          URL.revokeObjectURL(this.listPreviewObjectUrl);
        }

        this.listPreviewObjectUrl = fileUrl;
        this.listPreviewType.set(currentFileType);
        this.listPreviewUrl.set(fileUrl);
        this.isListPreviewOpened.set(true);
        this.attachListPreviewOverlay();
      },

      // Deliberately silent, no snackbar - the click just does nothing further, matching
      // hasPreview()'s "nothing to preview" state rather than surfacing a user-facing error for
      // what's usually a transient fetch failure. Still handled explicitly (not left as an
      // unhandled observer error) so it doesn't propagate as an uncaught exception.
      error: (error: unknown) => {
        // eslint-disable-next-line no-console
        console.error('[FileUploaderComponent] Failed to load list preview:', error);
      },
    });
  }

  public onDownloadFile(event?: Event): void {
    event?.stopPropagation();

    const currentImageId = this.currentImageId();

    if (!currentImageId) {
      return;
    }

    this.imageService.getFile(currentImageId, this.currentFileType()).subscribe({
      next: (fileUrl) => {
        const downloadLink = document.createElement('a');
        downloadLink.href = fileUrl;
        downloadLink.download = this.getDownloadFileName();
        downloadLink.click();

        setTimeout(() => {
          URL.revokeObjectURL(fileUrl);
        }, 500);
      },

      // Deliberately silent, no snackbar - see the identical choice in openListPreview above.
      error: (error: unknown) => {
        // eslint-disable-next-line no-console
        console.error('[FileUploaderComponent] Failed to download file:', error);
      },
    });
  }

  public onUploaderContainerClick(fileInput: HTMLInputElement | undefined, hasImage: string | null): void {
    if (!hasImage) {
      this.onUploadNewClick(fileInput);

      return;
    }

    // A selected document (e.g. the brief uploader's .pdf/.docx/.txt) has no pixels to enlarge -
    // enlarging it would point the overlay's <img> at an object URL of the document bytes, which
    // just renders as a broken image. isCurrentFileImage() mirrors the same check hasPreview()
    // already applies for 'list' mode's own preview button.
    if (this.showImagePreview() && this.isCurrentFileImage()) {
      this.onImageEnlarge();
    }
  }

  public onImageLoaded(): void {
    this.imageLoaded = true;
    this.imageError = false;
  }

  public onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    const currentSrc = img.src;

    if (currentSrc.includes(this.placeholderImage)) {
      return;
    }

    this.imageLoaded = true;
    this.imageError = true;
    img.src = this.placeholderImage;
  }

  // ImageLoaderDirective's own "I've retried and given up" signal (see appImageLoader's
  // (imageError) output in the template, and MAX_LOAD_ATTEMPTS/retryOrFallback in
  // image-loader.directive.ts) - distinct from onImageError above, which reacts to the native DOM
  // `error` event (the browser failing to load whatever [src] is currently bound to, e.g. a
  // broken external URL from processedImageUrl()). The directive already points the <img> at
  // placeholderImage itself before emitting this, so there's no img.src to set here - this only
  // needs to mark the attempt as settled. Without it, shouldShowSpinner()'s overlay and the
  // fileSizeLabel-adjacent skeleton below (both gated on !imageLoaded) have no failure path to
  // ever leave: they'd stay showing "loading" forever instead of settling into the
  // placeholder/"N/A" state the rest of the template already knows how to render.
  public onRemoteImageLoadFailed(): void {
    this.imageLoaded = true;
    this.imageError = true;
  }

  public enlargedOverlayDisplaySrc(): string {
    if (this.enlargedOverlayImageFailed()) {
      return this.placeholderImage;
    }

    if (this.imageError) {
      return this.placeholderImage;
    }

    const override = this.enlargedImageSrcOverride();

    if (override) {
      return override;
    }

    return this.currentImageUrl() ?? (this.isRemoteFileId() ? this.currentImageId() : this.processedImageUrl()) ?? '';
  }

  public enlargedOverlaySkeletonTheme(): Record<string, string> {
    return {
      'border-radius': '8px',
      height: 'min(90vh, 800px)',
      width: 'min(90vw, 800px)',
    };
  }

  public onEnlargedOverlayImageError(event: Event): void {
    event.stopPropagation();

    const img = event.target as HTMLImageElement;
    const currentSrc = img.src;

    if (currentSrc.includes(this.placeholderImage)) {
      return;
    }

    this.enlargedOverlayImageFailed.set(true);
    this.enlargedOverlayImageReady.set(true);
  }

  public onEnlargedOverlayImageLoaded(): void {
    this.enlargedOverlayImageReady.set(true);
  }

  // Single source of truth for fileName()/fileExtension()/currentFileType()/fileSizeLabel() above
  // for a remote (non-local-File) attachment, covering both display modes:
  // - 'list' mode: a one-shot call straight to the JSON metadata endpoint
  //   (ImageUtilitiesService.getFileMetadataInfo) - list mode never displays pixels, so there's
  //   no existing request to piggyback on, and no reason to also download (and discard) the
  //   file's bytes just to read its name/type/size. This only works for a modern id though - the
  //   metadata endpoint is typed to a UUID at the route level (unlike the byte-serving read
  //   routes, which are plain strings), so it can never resolve a legacy base64 id no matter what
  //   base64 support the backend adds there. See syncListFileMeta below for how that id is
  //   handled instead.
  // - 'media' mode: shares the exact same ref-counted request/cache entry
  //   ImageLoaderDirective is already making to display the pixels (see
  //   ImageUtilitiesService.getImageUrlAndSize's cache-key contract) - shareReplay means this is
  //   never a duplicate network request, just an extra subscriber to the same in-flight/cached
  //   response, correctly incrementing/decrementing the shared refCount so releasing here can
  //   never revoke a URL the directive (or another consumer) is still displaying.
  private syncRemoteFileMeta(): void {
    const currentImageId = this.currentImageId();
    const uploadedImage = this.uploadedImage();
    const displayMode = this.displayMode();

    if (!currentImageId || !this.isRemoteFileId()) {
      this.releaseRemoteFileMetaState();
      this.hasRemoteFileMetaError.set(false);
      this.remoteFileMeta.set(null);
      this.remoteFileMetaId.set(null);

      return;
    }

    if (uploadedImage && !uploadedImage.isMockFile) {
      // A real local File is selected/displayed - its name/type are already known synchronously
      // (see fileName()/fileExtension()/currentFileType() above), no remote metadata needed.
      this.releaseRemoteFileMetaState();
      this.hasRemoteFileMetaError.set(false);
      this.remoteFileMetaId.set(null);

      return;
    }

    if (this.remoteFileMetaId() === currentImageId) {
      return;
    }

    this.releaseRemoteFileMetaState();
    this.remoteFileMetaId.set(currentImageId);
    this.remoteFileMeta.set(null);
    this.hasRemoteFileMetaError.set(false);

    if (displayMode === 'list') {
      this.syncListFileMeta(currentImageId);

      return;
    }

    const { cacheKey, observable } = this.imageService.getImageUrlAndSize(currentImageId);

    this.mediaMetaCacheKey = cacheKey;

    this.remoteFileMetaSubscription = observable.subscribe({
      next: ({ fileName, mimeType, size }) => this.applyRemoteFileMeta(currentImageId, { fileName, mimeType, size }),

      error: () => this.applyRemoteFileMetaError(currentImageId),
    });
  }

  // Extracted out of syncRemoteFileMeta purely to keep that method under the linter's
  // max-lines-per-function limit - behaves exactly as it did inlined there.
  //
  // The metadata endpoint (getFileMetadataInfo) can never resolve a legacy id - it's typed to a
  // UUID at the route level (unlike the byte-serving read routes, which are plain strings), so no
  // base64 support the backend adds there would change that. The only option left for one is the
  // same blob+header/getLegacyFileName fallback 'media' mode and getFile's on-demand list preview
  // already rely on (see ImageUtilitiesService.toFileMetadata). Unlike 'media' mode, list mode has
  // no ImageLoaderDirective (or anything else) sharing this particular object URL, so it's
  // revoked immediately once fileName/mimeType/size are read out of it rather than kept around
  // unused - getFileMetadata isn't cached/ref-counted the way getImageUrlAndSize is, so nothing
  // else would ever revoke it otherwise.
  private syncListFileMeta(currentImageId: string): void {
    if (ImageUtilitiesService.isValidLegacyFileId(currentImageId)) {
      this.remoteFileMetaSubscription = this.imageService
        .getFileMetadata(currentImageId, this.currentFileType())
        .subscribe({
          next: ({ fileName, mimeType, size, url }) => {
            URL.revokeObjectURL(url);
            this.applyRemoteFileMeta(currentImageId, { fileName, mimeType, size });
          },

          error: () => this.applyRemoteFileMetaError(currentImageId),
        });

      return;
    }

    // 'list' mode never displays pixels, so unlike the 'media' branch in syncRemoteFileMeta
    // there's no shared ImageLoaderDirective cache entry to piggyback on - going straight to the
    // JSON metadata endpoint avoids downloading (and immediately discarding) the file's bytes
    // just to read its name/type/size, which the old getFileUrlAndSize('file')-based blob fetch
    // used to do.
    this.remoteFileMetaSubscription = this.imageService.getFileMetadataInfo(currentImageId).subscribe({
      next: ({ originalFilename, mimeType, sizeBytes }) =>
        this.applyRemoteFileMeta(currentImageId, { fileName: originalFilename, mimeType, size: sizeBytes }),

      error: () => this.applyRemoteFileMetaError(currentImageId),
    });
  }

  // Applied by both syncRemoteFileMeta() fetch branches above, guarded against a stale in-flight
  // response: currentImageId() may have moved on (e.g. rapid id A -> B -> A again) while a
  // request was still pending, in which case remoteFileMetaId() no longer matches the id this
  // particular response is for, and applying it now would clobber whatever the current id's own
  // fetch already settled.
  private applyRemoteFileMeta(idAtFetchTime: string, meta: IRemoteFileMeta): void {
    if (this.remoteFileMetaId() !== idAtFetchTime) {
      return;
    }

    this.hasRemoteFileMetaError.set(false);
    this.remoteFileMeta.set(meta);
  }

  private applyRemoteFileMetaError(idAtFetchTime: string): void {
    if (this.remoteFileMetaId() !== idAtFetchTime) {
      return;
    }

    this.hasRemoteFileMetaError.set(true);
    this.remoteFileMeta.set(null);
  }

  private releaseRemoteFileMetaState(): void {
    if (this.mediaMetaCacheKey) {
      this.imageService.releaseImage(this.mediaMetaCacheKey);
      this.mediaMetaCacheKey = undefined;
    }

    if (this.remoteFileMetaSubscription) {
      this.remoteFileMetaSubscription.unsubscribe();
      this.remoteFileMetaSubscription = undefined;
    }
  }

  private attachListPreviewOverlay(): void {
    if (this.listPreviewOverlayRef) {
      return;
    }

    const template = this.listPreviewOverlayTemplate();

    if (!template) {
      return;
    }

    const overlayRef = this.createFullscreenOverlayRef();

    overlayRef.attach(new TemplatePortal(template, this.viewContainerRef));
    this.listPreviewOverlayRef = overlayRef;
  }

  private attachMediaEnlargeOverlay(): void {
    if (this.mediaEnlargeOverlayRef) {
      return;
    }

    const template = this.enlargedImageOverlayTemplate();

    if (!template) {
      return;
    }

    this.syncEnlargedImageSrcOverrideFromThumbnail();
    this.enlargedOverlayImageFailed.set(false);
    this.enlargedOverlayImageReady.set(false);

    const overlayRef = this.createFullscreenOverlayRef();

    overlayRef.attach(new TemplatePortal(template, this.viewContainerRef));
    this.mediaEnlargeOverlayRef = overlayRef;

    queueMicrotask(() => {
      this.syncEnlargedImageSrcOverrideFromThumbnail();
    });
  }

  private createFullscreenOverlayRef(): OverlayRef {
    return this.overlay.create({
      hasBackdrop: false,
      height: '100%',
      maxHeight: '100vh',
      maxWidth: '100vw',
      panelClass: 'file-uploader-overlay-panel',
      positionStrategy: this.overlay.position().global().left('0').top('0'),
      scrollStrategy: this.overlay.scrollStrategies.block(),
      width: '100%',
    });
  }

  private detachListPreviewOverlay(): void {
    if (!this.listPreviewOverlayRef) {
      return;
    }

    this.listPreviewOverlayRef.dispose();
    this.listPreviewOverlayRef = undefined;
  }

  private detachMediaEnlargeOverlay(): void {
    if (!this.mediaEnlargeOverlayRef) {
      return;
    }

    this.mediaEnlargeOverlayRef.dispose();
    this.mediaEnlargeOverlayRef = undefined;
    this.enlargedImageSrcOverride.set(null);
    this.enlargedOverlayImageFailed.set(false);
    this.enlargedOverlayImageReady.set(false);
  }

  private syncEnlargedImageSrcOverrideFromThumbnail(): void {
    const img = this.thumbnailImageRef()?.nativeElement;
    const resolved = img?.currentSrc || img?.src;

    if (!resolved || resolved.includes(this.placeholderImage) || resolved === ImageUtilitiesService.transparentImage) {
      this.enlargedImageSrcOverride.set(null);

      return;
    }

    this.enlargedImageSrcOverride.set(resolved);
  }

  private resetState(): void {
    if (this.isImageEnlarged) {
      this.isImageEnlarged = false;
      this.detachMediaEnlargeOverlay();
    }

    if (this.isListPreviewOpened()) {
      this.closeListPreview();
    }

    this.releaseRemoteFileMetaState();
    this.hasRemoteFileMetaError.set(false);
    this.remoteFileMeta.set(null);
    this.remoteFileMetaId.set(null);
    this.fileForm()?.reset();

    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = undefined;
    }

    this.updateState({
      file: null,
      imageId: null,
    });
    this.imageLoaded = false;
    this.clearFileInput();

    this.isStateReset.set(!this.isStateReset());
  }

  private updateState(newState: Partial<IFileUploadState>): void {
    this.state.update((current) => ({ ...current, ...newState }));
  }

  private clearSelectedFileAndKeepErrors(errors: ValidationErrors | null, emitEvent = true): void {
    const fileForm = this.fileForm();

    this.file.set(null);
    this.updateState({ file: null });
    this.imageLoaded = false;
    this.imageError = false;

    if (fileForm) {
      fileForm.patchValue(null, { emitEvent });
      fileForm.setErrors(errors);
    }

    this.clearFileInput();
    this.isStateReset.set(!this.isStateReset());
  }

  private isImageFormValid(file: File | null, emitEvent = true): boolean {
    const fileForm = this.fileForm();
    this.displayError = true;

    if (!fileForm) {
      return true;
    }

    fileForm?.patchValue(file, { emitEvent });

    if (!fileForm.valid) {
      this.clearSelectedFileAndKeepErrors(fileForm.errors, emitEvent);

      return false;
    }

    return true;
  }

  public onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.disabled() && !this.isViewMode()) {
      this.isDragging.set(true);
    }
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.disabled() && !this.isViewMode()) {
      this.isDragging.set(true);
    }
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.disabled() || this.isViewMode()) {
      return;
    }

    this.isDragging.set(false);
    this.uploadFile(event.dataTransfer?.files[0]);
  }

  private uploadFile(file: File | undefined, event?: Event): void {
    if (!file || !this.isFileTypeValid(file) || !this.isImageFormValid(file)) {
      return;
    }

    this.updateState({ file, isUploading: true });
    this.imageLoaded = false;
    // Clear a stale remote size/name/mime-type from whatever was previously attached before this
    // upload's own metadata (or, for a real local File, no remote metadata at all) takes over -
    // mirrors the old remoteFileSize.set(null) reset here, generalized to the unified metadata
    // signal. syncRemoteFileMeta()'s own "real local File selected" branch intentionally leaves
    // this alone (see its comment) since it only fires on id/mode changes, not on every upload.
    this.remoteFileMeta.set(null);

    const fileType = FileUploaderService.getFileTypeByFile(file);

    const customUploadRequest = this.uploadRequest();
    const uploadFileRequest$: Observable<string> = customUploadRequest
      ? customUploadRequest(file, fileType)
      : this.imageService.uploadFile(file, fileType);

    uploadFileRequest$.pipe(finalize(() => this.updateState({ isUploading: false }))).subscribe({
      next: (id) => {
        this.updateState({ imageId: id, isUploading: false });
        this.imageSelected.emit({ file, id });

        if (this.clearAfterUpload()) {
          this.resetState();
        }
      },

      error: () => {
        this.updateState({ file: null, imageId: null });

        const fileInput = event?.target as HTMLInputElement;

        if (fileInput) {
          fileInput.value = '';
        }
      },
    });
  }

  private isFileTypeValid(file: File): boolean {
    const extension = file.name.split('.').pop()?.toLowerCase();

    if (!extension || !this.allowedFileExtensions().includes(extension)) {
      this.displayError = true;
      this.setFileFormErrors({ invalidFileExtension: true });
      this.clearSelectedFileAndKeepErrors(this.fileForm()?.errors ?? null);

      return false;
    }

    this.clearInvalidExtensionError();

    return true;
  }

  private clearInvalidExtensionError(): void {
    const fileForm = this.fileForm();

    if (!fileForm?.errors?.['invalidFileExtension']) {
      return;
    }

    const { invalidFileExtension, ...remainingErrors } = fileForm.errors;

    if (invalidFileExtension && Object.keys(remainingErrors).length === 0) {
      fileForm.setErrors(null);

      return;
    }

    fileForm.setErrors(remainingErrors);
  }

  private setFileFormErrors(errors: Record<string, boolean>): void {
    const fileForm = this.fileForm();

    if (!fileForm) {
      return;
    }

    fileForm.setErrors({
      ...(fileForm.errors ?? {}),
      ...errors,
    });
  }

  private clearFileInput(): void {
    const fileInput = this.fileInputRef()?.nativeElement;

    if (fileInput) {
      fileInput.value = '';
    }
  }

  private getDownloadFileName(): string {
    const extension = this.fileExtension();
    const name = this.fileName();

    if (!extension) {
      return name;
    }

    return `${name}.${extension}`;
  }
}
